import {
  ArrowsAltOutlined,
  DownOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Dropdown,
  Menu,
  Popover,
  Row,
  Space,
  Spin,
} from "antd";
import moment from "moment";
import { useState } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as ReTooltip,
  XAxis,
  YAxis,
} from "recharts";

import { useEndTime, useStartTime } from "../DateContext/DateContext";
// eslint-disable-next-line import/extensions
import TimePeriod from "./types";
// eslint-disable-next-line import/extensions
import useActiveMemberMetrics from "./useActiveMemberMetrics";

/**
 * DiscordActiveMembersCard props
 * guildId: Discord guild id
 * span: the number of columns we want the card to span in the page layout
 */
interface Props {
  guildId: string | null;
  span: number;
  expandActiveMembers: boolean;
  setExpandActiveMembers: React.Dispatch<React.SetStateAction<boolean>>;
}
/**
 *
 * @param props - @see Props interface above
 * @returns A component that shows the Discord active members over time grouped by day or week
 */
export default function DiscordActiveMembersCard(props: Props) {
  const { guildId, span, expandActiveMembers, setExpandActiveMembers } = props;
  const { startTime, setStartTime } = useStartTime();
  const { endTime, setEndTime } = useEndTime();
  const [timePeriod, setTimePeriod] = useState<TimePeriod>(TimePeriod.Days);

  const { activeMemberMetrics, loading } = useActiveMemberMetrics(
    startTime,
    endTime,
    guildId,
    timePeriod
  );

  const start = moment(startTime);
  const end = moment(endTime);
  const numberOfWeeks = moment.duration(end.diff(start)).asWeeks();

  function handleMenuClick(e: any) {
    if (e.key === "1") {
      return;
    }
    if (e.key === "2") {
      setTimePeriod(TimePeriod.Days);
    }
    if (e.key === "3") {
      setTimePeriod(TimePeriod.Week);
    }
  }

  const menu = (
    <Menu onClick={handleMenuClick}>
      <Menu.Item key="2">{TimePeriod.Days}</Menu.Item>
      {numberOfWeeks > 1 ? (
        <Menu.Item key="3">{TimePeriod.Week}</Menu.Item>
      ) : null}
    </Menu>
  );

  const popupContent = (text: string) => (
    <div>
      <p>{text}</p>
    </div>
  );

  const dataKey = (): string => {
    if (timePeriod === "days" && !loading) return "for_date";
    if (timePeriod === "week" && !loading) return "weekly";
    if (timePeriod === "week" && loading) return "for_date";
    return "weekly";
  };

  return (
    <Spin spinning={loading}>
      <Card
        style={{ marginBottom: 40 }}
        title="Active members over time"
        bordered={false}
        extra={
          <>
            <Space>
              <p style={{ marginTop: 10 }}>Group by</p>
              <Dropdown className="joyride-date" overlay={menu}>
                <Button>
                  {timePeriod} <DownOutlined />
                </Button>
              </Dropdown>
              <Button
                onClick={() => setExpandActiveMembers(!expandActiveMembers)}
              >
                <div className="-mt-1">
                  <ArrowsAltOutlined />
                </div>
              </Button>
              <Popover
                content={popupContent(
                  "Members that posted a message or sent an emoji reaction in a public channel, over time."
                )}
                className="ml-3"
              >
                <InfoCircleOutlined />
              </Popover>
            </Space>
          </>
        }
      >
        <Row>
          <Col span={span}>
            <div
              style={{
                width: "100%",
                height: expandActiveMembers ? "500px" : "250px",
              }}
            >
              <ResponsiveContainer>
                <LineChart
                  width={430}
                  data={
                    activeMemberMetrics === null
                      ? undefined
                      : activeMemberMetrics
                  }
                  margin={{
                    top: 5,
                    right: 5,
                    left: 0,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey={
                      dataKey()
                      // timePeriod === "days" && !loading ? "for_date" : "weekly"
                    }
                    tickFormatter={(date) => {
                      return moment(date).format("MM/DD");
                    }}
                  />
                  <YAxis domain={["auto", "auto"]} />
                  <Legend />
                  <ReTooltip />
                  <Line
                    type="monotone"
                    dataKey="total_count"
                    name="# of active members"
                    stroke="#8884d8"
                    activeDot={{ r: 8 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Col>
        </Row>
      </Card>
    </Spin>
  );
}
